<template>
  <div
    class="sidebar-shell"
    :class="{ 'sidebar-shell--open': isPanelOpen }"
    :style="{
      background: theme.background,
      color: theme.text,
      '--sidebar-panel-width': `${panelWidth}px`,
      '--sidebar-total-width': `${totalSidebarWidth}px`
    }"
  >
    <!-- ====================================================== -->
    <!-- LEVEL 1: TOP-LEVEL ICON RAIL -->
    <!-- ====================================================== -->

    <v-card
      flat
      class="sidebar-rail"
      :style="{
        background: theme.primary,
        color: theme.text
      }"
    >
      <v-list
        nav
        density="compact"
        class="sidebar-rail-list"
        :style="{
          background: theme.primary,
          color: theme.text
        }"
      >
        <template
          v-for="item in visibleRootMenu"
          :key="item.id"
        >
          <v-tooltip
            location="right"
            :text="item.title"
          >
            <template #activator="{ props }">
              <v-list-item
                v-bind="props"
                class="sidebar-rail-item"
                :class="{
                  'sidebar-rail-item--active':
                    selectedRootId === item.id
                }"
                :active="selectedRootId === item.id"
                @click="selectRootItem(item)"
              >
                <template #default>
                  <div class="d-flex justify-center align-center w-100">
                    <v-icon
                      :icon="item.icon || 'mdi-circle-outline'"
                      size="24"
                    />
                  </div>
                </template>
              </v-list-item>
            </template>
          </v-tooltip>
        </template>
      </v-list>
    </v-card>

    <!-- ====================================================== -->
    <!-- LEVEL 2: SLIDING CHILD MENU -->
    <!-- ====================================================== -->

    <v-slide-x-transition>
      <v-card
        v-if="isPanelOpen && selectedRoot"
        flat
        class="sidebar-panel"
        :style="{
          background: theme.background,
          color: theme.text,
          width: `${panelWidth}px`,
          minWidth: `${panelWidth}px`,
          maxWidth: `${panelWidth}px`
        }"
      >
        <!-- PANEL HEADER -->
        <div
          class="sidebar-panel-header"
          :style="{
            background: theme.primary,
            color: theme.text
          }"
        >
          <div class="d-flex align-center ga-2 min-width-0">
            <v-icon
              :icon="selectedRoot.icon || 'mdi-circle-outline'"
              size="22"
            />

            <div class="sidebar-panel-title">
              {{ selectedRoot.title }}
            </div>
          </div>

          <v-tooltip
            location="bottom"
            :text="
              currentLanguage === 'fr'
                ? 'Fermer le menu'
                : 'Close menu'
            "
          >
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                icon="mdi-chevron-left"
                variant="text"
                size="small"
                @click="closePanel"
              />
            </template>
          </v-tooltip>
        </div>

        <!-- SEARCH -->
        <div class="sidebar-search">
          <v-text-field
            v-model="search"
            :label="t('search')"
            variant="outlined"
            density="compact"
            prepend-inner-icon="mdi-magnify"
            clearable
            hide-details
          />
        </div>

        <v-divider />

        <!-- CHILD MENU -->
        <div class="sidebar-panel-content">
          <v-list
            v-model:opened="open"
            nav
            density="compact"
            class="sidebar-child-list"
            :style="{
              background: theme.background,
              color: theme.text
            }"
          >
            <template
              v-for="child in filteredSelectedChildren"
              :key="child.id"
            >
              <!-- CHILD GROUP -->
              <v-list-group
                v-if="child.children?.length"
                :value="child.id"
              >
                <template #activator="{ props }">
                  <v-list-item
                    v-bind="props"
                    :prepend-icon="
                      child.icon ||
                      'mdi-folder-outline'
                    "
                    :title="child.title"
                    class="sidebar-group-item"
                    :class="{
                      'sidebar-group-item--active':
                        containsActiveRoute(child)
                    }"
                  />
                </template>

                <!-- GRANDCHILDREN -->
                <template
                  v-for="sub in child.children"
                  :key="sub.id"
                >
                  <!-- SUPPORT AN EXTRA NESTED LEVEL -->
                  <v-list-group
                    v-if="sub.children?.length"
                    :value="sub.id"
                  >
                    <template #activator="{ props }">
                      <v-list-item
                        v-bind="props"
                        :prepend-icon="
                          sub.icon ||
                          'mdi-folder-outline'
                        "
                        :title="sub.title"
                        class="sidebar-subgroup-item"
                        :class="{
                          'sidebar-group-item--active':
                            containsActiveRoute(sub)
                        }"
                      />
                    </template>

                    <v-list-item
                      v-for="leaf in sub.children"
                      :key="leaf.id"
                      :prepend-icon="
                        leaf.icon ||
                        'mdi-circle-small'
                      "
                      :title="leaf.title"
                      link
                      :to="leaf.to"
                      :class="{
                        'active-item':
                          isRouteActive(leaf.to)
                      }"
                    />
                  </v-list-group>

                  <!-- NORMAL GRANDCHILD -->
                  <v-list-item
                    v-else
                    :prepend-icon="
                      sub.icon ||
                      'mdi-circle-small'
                    "
                    :title="sub.title"
                    link
                    :to="sub.to"
                    :class="{
                      'active-item':
                        isRouteActive(sub.to)
                    }"
                  />
                </template>
              </v-list-group>

              <!-- NORMAL CHILD -->
              <v-list-item
                v-else
                :prepend-icon="
                  child.icon ||
                  'mdi-circle-small'
                "
                :title="child.title"
                link
                :to="child.to"
                :class="{
                  'active-item':
                    isRouteActive(child.to)
                }"
              />
            </template>

            <div
              v-if="filteredSelectedChildren.length === 0"
              class="sidebar-empty"
            >
              <v-icon
                icon="mdi-magnify-close"
                size="28"
                class="mb-2"
              />

              <div>
                {{
                  currentLanguage === 'fr'
                    ? 'Aucun menu trouvé'
                    : 'No menu items found'
                }}
              </div>
            </div>
          </v-list>
        </div>

        <!-- ================================================== -->
        <!-- RESIZE HANDLE -->
        <!-- ================================================== -->

        <div
          class="sidebar-resize-handle"
          :class="{
            'sidebar-resize-handle--active':
              isResizing
          }"
          @pointerdown="startResize"
        >
          <div class="sidebar-resize-grip" />
        </div>
      </v-card>
    </v-slide-x-transition>
  </div>
</template>

<script setup>
import { ref, watch, computed, onBeforeUnmount } from "vue"
import { useRoute, useRouter } from "vue-router"
import { useAuthStore } from "@/store/auth"
import { useI18n } from 'vue-i18n'

import { useLangStore } from '@/store/lang'
import { useThemeStore } from "@/store/auth"
import toast from 'vue-toastification'
const langStore = useLangStore()
const themeStore = useThemeStore()
const theme = computed(() => themeStore.themeStyles)
const lang = computed(() => langStore.currentLanguage)

const auth = useAuthStore()
const { t, locale } = useI18n()

const route = useRoute()
const router = useRouter()

const emit = defineEmits([
  'resize',
  'panel-state'
])

// SEARCH
const search = ref("")
const open = ref([])

watch(
  () => langStore.currentLanguage,
  (newLang) => {
    locale.value = newLang
    },
    { immediate: true }
  )
/* -------------------------------------------------------
   REACTIVE MENU (updates when lang changes)
------------------------------------------------------- */
const menu = computed(() => [
  {
    id: "Procurement",
    title: lang.value === 'en' ? 'Procurement' : 'Approvisionnement',
    icon: "mdi-cart-variant",
    children: [
      {
        id: "Setup/procurement",
        title: lang.value === 'en' ? 'Setup' : 'Configuration',
        icon: "mdi-cog-outline",
        children: [
          {
            id: "proc-suppliers",
            title: lang.value === 'en' ? 'Suppliers' : 'Fournisseurs',
            icon: "mdi-truck-outline",
            to: router.resolve({
              name: "ProcurementSuppliers",
              params: { lang: lang.value }
            }).href,
          },
          {
            id: "proc-categories",
            title: lang.value === 'en' ? 'Categories' : 'Catégories',
            icon: "mdi-shape-outline",
            to: router.resolve({
              name: "ProcurementCategories",
              params: { lang: lang.value }
            }).href,
          },
          {
            id: "proc-report-designer",
            title: lang.value === 'en'
              ? 'Document & Report Designer'
              : 'Concepteur de documents et rapports',
            icon: "mdi-file-document-edit-outline",
            to: router.resolve({
              name: "ProcurementReportDesigner",
              params: { lang: lang.value }
            }).href,
          },
        ]
      },
      {
        id: "Requisitions/procurement",
        title: lang.value === 'en' ? 'Requisitions' : 'Réquisitions',
        icon: "mdi-clipboard-text-outline",
        children: [
          {
            id: "proc-purchase-requisitions",
            title: lang.value === 'en'
              ? 'Purchase Requisitions'
              : "Réquisitions d'achat",
            icon: "mdi-clipboard-list-outline",
            to: router.resolve({
              name: "ProcurementRequisitions",
              params: { lang: lang.value }
            }).href,
          },
        ]
      },
      {
        id: "Purchasing/procurement",
        title: lang.value === 'en' ? 'Purchasing' : 'Achats',
        icon: "mdi-cart-outline",
        children: [
          {
            id: "proc-purchase-orders",
            title: lang.value === 'en'
              ? 'Purchase Orders'
              : 'Bons de commande',
            icon: "mdi-file-sign",
            to: router.resolve({
              name: "ProcurementOrders",
              params: { lang: lang.value }
            }).href,
          },
        ]
      },
      {
        id: "Receiving/procurement",
        title: lang.value === 'en' ? 'Receiving' : 'Réception',
        icon: "mdi-package-variant-closed-check",
        children: [
          {
            id: "proc-goods-receipts",
            title: lang.value === 'en'
              ? 'Goods Receipts'
              : 'Réceptions de marchandises',
            icon: "mdi-package-variant",
            to: router.resolve({
              name: "ProcurementReceipts",
              params: { lang: lang.value }
            }).href,
          },
        ]
      },
      {
        id: "Invoices/procurement",
        title: lang.value === 'en'
          ? 'Supplier Invoices'
          : 'Factures fournisseurs',
        icon: "mdi-receipt-text-outline",
        children: [
          {
            id: "proc-supplier-invoices",
            title: lang.value === 'en'
              ? 'Supplier Invoices'
              : 'Factures fournisseurs',
            icon: "mdi-receipt-text",
            to: router.resolve({
              name: "ProcurementInvoices",
              params: { lang: lang.value }
            }).href,
          },
        ]
      },
    ]
  },
])


/* -------------------------------------------------------
   LANGUAGE
------------------------------------------------------- */
const currentLanguage = computed(() =>
  langStore.currentLanguage || 'en'
)

/* -------------------------------------------------------
   TOP-LEVEL SELECTION
------------------------------------------------------- */
const selectedRootId = ref('Procurement')
const isPanelOpen = ref(true)

/* -------------------------------------------------------
   RESIZABLE SECOND PANE
------------------------------------------------------- */
const ICON_RAIL_WIDTH = 64
const DEFAULT_PANEL_WIDTH = 280
const MIN_PANEL_WIDTH = 220
const MAX_PANEL_WIDTH = 520

const panelWidth =
  ref(DEFAULT_PANEL_WIDTH)

const isResizing =
  ref(false)

const resizeStartX =
  ref(0)

const resizeStartWidth =
  ref(DEFAULT_PANEL_WIDTH)

const totalSidebarWidth =
  computed(() =>
    isPanelOpen.value
      ? ICON_RAIL_WIDTH + panelWidth.value
      : ICON_RAIL_WIDTH
  )

function emitSidebarWidth() {
  const detail = {
    railWidth:
      ICON_RAIL_WIDTH,

    panelWidth:
      isPanelOpen.value
        ? panelWidth.value
        : 0,

    totalWidth:
      totalSidebarWidth.value,

    isOpen:
      isPanelOpen.value
  }

  emit(
    'resize',
    detail
  )

  emit(
    'panel-state',
    detail
  )

  window.dispatchEvent(
    new CustomEvent(
      'nexgbits-sidebar-resize',
      {
        detail
      }
    )
  )
}

function getPointerX(event) {
  return Number(event?.clientX ?? 0)
}

let activeResizeTarget =
  null

function startResize(event) {
  if (!isPanelOpen.value) {
    return
  }

  if (
    event.pointerType === 'mouse' &&
    event.button !== 0
  ) {
    return
  }

  event.preventDefault()
  event.stopPropagation()

  isResizing.value =
    true

  resizeStartX.value =
    getPointerX(event)

  resizeStartWidth.value =
    panelWidth.value

  activeResizeTarget =
    event.currentTarget

  activeResizeTarget
    ?.setPointerCapture?.(
      event.pointerId
    )

  document.body.classList.add(
    'nexgbits-sidebar-resizing'
  )

  window.addEventListener(
    'pointermove',
    handleResize
  )

  window.addEventListener(
    'pointerup',
    stopResize
  )

  window.addEventListener(
    'pointercancel',
    stopResize
  )
}

function handleResize(event) {
  if (!isResizing.value) {
    return
  }

  event.preventDefault()

  const currentX =
    getPointerX(event)

  const delta =
    currentX -
    resizeStartX.value

  panelWidth.value =
    Math.min(
      MAX_PANEL_WIDTH,
      Math.max(
        MIN_PANEL_WIDTH,
        resizeStartWidth.value +
          delta
      )
    )

  emitSidebarWidth()
}

function stopResize(event) {
  if (!isResizing.value) {
    return
  }

  isResizing.value =
    false

  if (
    activeResizeTarget &&
    event?.pointerId != null
  ) {
    try {
      activeResizeTarget
        .releasePointerCapture?.(
          event.pointerId
        )
    } catch {
      // Ignore if capture was already released.
    }
  }

  activeResizeTarget =
    null

  document.body.classList.remove(
    'nexgbits-sidebar-resizing'
  )

  window.removeEventListener(
    'pointermove',
    handleResize
  )

  window.removeEventListener(
    'pointerup',
    stopResize
  )

  window.removeEventListener(
    'pointercancel',
    stopResize
  )

  emitSidebarWidth()
}

/* -------------------------------------------------------
   PERMISSION FILTERING
------------------------------------------------------- */
function canAccessItem(item) {
  if (!item) return false

  if (item.public === true) {
    return true
  }

  if (
    item.permission &&
    !auth.hasPermission(item.permission)
  ) {
    return false
  }

  return true
}

function filterByAccess(items) {
  return (items || [])
    .map(item => {
      if (!canAccessItem(item)) {
        return null
      }

      if (item.children?.length) {
        const children =
          filterByAccess(item.children)

        if (children.length === 0) {
          return item.to
            ? { ...item, children: undefined }
            : null
        }

        return {
          ...item,
          children
        }
      }

      return item
    })
    .filter(Boolean)
}

/* -------------------------------------------------------
   ROOT MENU
------------------------------------------------------- */
const visibleRootMenu = computed(() =>
  filterByAccess(menu.value)
)

const selectedRoot = computed(() =>
  visibleRootMenu.value.find(
    item =>
      String(item.id) ===
      String(selectedRootId.value)
  ) || null
)

/* -------------------------------------------------------
   SEARCH CURRENT ROOT CHILDREN
------------------------------------------------------- */
function filterItems(items, query) {
  const q =
    String(query || '')
      .trim()
      .toLowerCase()

  return (items || [])
    .map(item => {
      if (!canAccessItem(item)) {
        return null
      }

      const title =
        String(item.title || '')
          .toLowerCase()

      const match =
        !q ||
        title.includes(q)

      if (item.children?.length) {
        const filteredChildren =
          filterItems(
            item.children,
            q
          )

        if (
          filteredChildren.length > 0
        ) {
          return {
            ...item,
            children: filteredChildren
          }
        }

        return match
          ? {
              ...item,
              children:
                filterByAccess(
                  item.children
                )
            }
          : null
      }

      return match
        ? item
        : null
    })
    .filter(Boolean)
}

const filteredSelectedChildren =
  computed(() => {
    if (!selectedRoot.value) {
      return []
    }

    return filterItems(
      selectedRoot.value.children || [],
      search.value
    )
  })

/* -------------------------------------------------------
   ROUTE HELPERS
------------------------------------------------------- */
function normalizePath(value) {
  if (!value) {
    return ''
  }

  try {
    return new URL(
      value,
      window.location.origin
    ).pathname.replace(/\/+$/, '')
  } catch {
    return String(value)
      .split('?')[0]
      .split('#')[0]
      .replace(/\/+$/, '')
  }
}

function isRouteActive(to) {
  if (!to) {
    return false
  }

  return (
    normalizePath(route.path) ===
    normalizePath(to)
  )
}

function containsActiveRoute(item) {
  if (!item) {
    return false
  }

  if (
    item.to &&
    isRouteActive(item.to)
  ) {
    return true
  }

  return (
    item.children || []
  ).some(containsActiveRoute)
}

function findRootForCurrentRoute() {
  return visibleRootMenu.value.find(
    root =>
      containsActiveRoute(root)
  ) || null
}

/* -------------------------------------------------------
   OPEN GROUP IDS
------------------------------------------------------- */
function collectGroupIds(
  items,
  results = []
) {
  ;(items || []).forEach(item => {
    if (item.children?.length) {
      results.push(item.id)

      collectGroupIds(
        item.children,
        results
      )
    }
  })

  return results
}

function refreshOpenGroups() {
  open.value =
    collectGroupIds(
      filteredSelectedChildren.value
    )
}

/* -------------------------------------------------------
   ROOT ICON CLICK
------------------------------------------------------- */
async function selectRootItem(item) {
  if (!item) {
    return
  }

  if (!item.children?.length) {
    if (item.to) {
      await router.push(item.to)
    }

    return
  }

  if (
    selectedRootId.value === item.id &&
    isPanelOpen.value
  ) {
    closePanel()
    return
  }

  selectedRootId.value =
    item.id

  search.value =
    ''

  isPanelOpen.value =
    true

  refreshOpenGroups()

  emitSidebarWidth()
}

function closePanel() {
  isPanelOpen.value =
    false

  search.value =
    ''

  open.value =
    []

  emitSidebarWidth()
}

/* -------------------------------------------------------
   KEEP ACTIVE ROUTE'S ROOT SELECTED
------------------------------------------------------- */
watch(
  () => route.path,
  () => {
    const root =
      findRootForCurrentRoute()

    if (root) {
      selectedRootId.value =
        root.id

      isPanelOpen.value =
        true
    } else if (
      !selectedRootId.value
    ) {
      selectedRootId.value =
        'Procurement'

      isPanelOpen.value =
        true
    }
  },
  {
    immediate: true
  }
)

/* -------------------------------------------------------
   SEARCH: EXPAND MATCHING GROUPS
------------------------------------------------------- */
watch(
  search,
  () => {
    refreshOpenGroups()
  }
)

/* -------------------------------------------------------
   MENU/LANGUAGE/PERMISSION CHANGES
------------------------------------------------------- */
watch(
  visibleRootMenu,
  roots => {
    if (
      roots.length &&
      !roots.some(
        root =>
          String(root.id) ===
          String(selectedRootId.value)
      )
    ) {
      selectedRootId.value =
        roots.some(
          root =>
            String(root.id) ===
            'Procurement'
        )
          ? 'Procurement'
          : roots[0].id
    }

    if (
      roots.length &&
      selectedRootId.value
    ) {
      isPanelOpen.value =
        true

      refreshOpenGroups()
    }
  },
  {
    immediate: true
  }
)

watch(
  visibleRootMenu,
  roots => {
    if (!roots.length) {
      selectedRootId.value =
        null

      isPanelOpen.value =
        false

      return
    }

    const selectedStillVisible =
      roots.some(
        root =>
          String(root.id) ===
          String(selectedRootId.value)
      )

    if (!selectedStillVisible) {
      const routeRoot =
        findRootForCurrentRoute()

      selectedRootId.value =
        routeRoot?.id ||
        (
          roots.some(
            root =>
              String(root.id) ===
              'Procurement'
          )
            ? 'Procurement'
            : roots[0]?.id
        )

      isPanelOpen.value =
        Boolean(
          selectedRootId.value
        )
    }
  }
)
/* -------------------------------------------------------
   RESIZE CLEANUP
------------------------------------------------------- */
onBeforeUnmount(() => {
  stopResize()

})

watch(
  totalSidebarWidth,
  () => {
    emitSidebarWidth()
  },
  {
    immediate: true
  }
)

</script>

<style scoped>
.sidebar-shell {
  display: flex;
  align-items: stretch;
  width: 64px;
  min-height: 100%;
  overflow: visible;
  transition: width 160ms ease;
}

.sidebar-shell--open {
  width: var(--sidebar-total-width);
}

/* ============================================================
   ICON RAIL
============================================================ */

.sidebar-rail {
  width: 64px;
  min-width: 64px;
  border-radius: 0;
  z-index: 2;
}

.sidebar-rail-list {
  padding: 8px 6px;
}

.sidebar-rail-item {
  width: 52px;
  min-height: 48px !important;
  margin: 4px 0;
  padding: 0 !important;
  border-radius: 10px !important;
  justify-content: center;
}

.sidebar-rail-item :deep(.v-list-item__content) {
  display: flex;
  justify-content: center;
  align-items: center;
}

.sidebar-rail-item--active {
  background:
    rgba(255, 255, 255, 0.18) !important;
}

.sidebar-rail-item:hover {
  background:
    rgba(255, 255, 255, 0.12) !important;
}

/* ============================================================
   SLIDING PANEL
============================================================ */

.sidebar-panel {
  position: relative;
  flex: 0 0 auto;
  width: var(--sidebar-panel-width);
  min-width: var(--sidebar-panel-width);
  max-width: var(--sidebar-panel-width);
  border-radius: 0;
  border-right:
    1px solid rgba(0, 0, 0, 0.08);
  box-shadow:
    6px 0 18px rgba(0, 0, 0, 0.08);
  z-index: 1;
}

.sidebar-panel-header {
  min-height: 56px;
  padding: 8px 10px 8px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.sidebar-panel-title {
  min-width: 0;
  max-width: 185px;
  font-size: 15px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-search {
  padding: 12px;
}

.sidebar-panel-content {
  max-height: calc(100vh - 130px);
  overflow-y: auto;
  overflow-x: hidden;
}

.sidebar-child-list {
  padding: 6px;
}

.sidebar-group-item,
.sidebar-subgroup-item,
.sidebar-child-list :deep(.v-list-item) {
  min-height: 38px !important;
  border-radius: 7px;
  margin-bottom: 2px;
}

.sidebar-group-item {
  font-weight: 600;
}

.sidebar-subgroup-item {
  font-weight: 500;
}

.sidebar-group-item--active {
  background:
    rgba(25, 118, 210, 0.10) !important;
}

/* ============================================================
   ACTIVE ROUTE
============================================================ */

.active-item {
  background:
    rgba(25, 118, 210, 0.16) !important;
  color: #1976d2 !important;
  font-weight: 700;
}

.active-item :deep(.v-icon) {
  color: #1976d2 !important;
}

/* ============================================================
   NESTING
============================================================ */

.sidebar-child-list
  :deep(.v-list-group__items .v-list-item) {
  padding-inline-start: 24px !important;
}

.sidebar-child-list
  :deep(
    .v-list-group__items
    .v-list-group__items
    .v-list-item
  ) {
  padding-inline-start: 38px !important;
}

.sidebar-empty {
  padding: 32px 12px;
  text-align: center;
  opacity: 0.7;
  font-size: 14px;
}

.min-width-0 {
  min-width: 0;
}

/* ============================================================
   RESIZE HANDLE
============================================================ */

.sidebar-resize-handle {
  position: absolute;
  top: 0;
  right: 0;
  width: 12px;
  height: 100%;
  cursor: ew-resize;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
  touch-action: none;
}

.sidebar-resize-handle::before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: 3px;
  width: 2px;
  background:
    rgba(0, 0, 0, 0.08);
  transition:
    background 120ms ease,
    width 120ms ease;
}

.sidebar-resize-handle:hover::before,
.sidebar-resize-handle--active::before {
  width: 3px;
  background:
    rgba(25, 118, 210, 0.55);
}

.sidebar-resize-grip {
  width: 3px;
  height: 42px;
  border-radius: 999px;
  background:
    rgba(0, 0, 0, 0.18);
  opacity: 0;
  transition: opacity 120ms ease;
}

.sidebar-resize-handle:hover
.sidebar-resize-grip,
.sidebar-resize-handle--active
.sidebar-resize-grip {
  opacity: 1;
}

:global(body.nexgbits-sidebar-resizing) {
  cursor: ew-resize !important;
  user-select: none !important;
}

:global(body.nexgbits-sidebar-resizing *) {
  cursor: ew-resize !important;
}

:global(body.nexgbits-sidebar-resizing) .sidebar-shell {
  transition: none !important;
}


/* ============================================================
   SMALL VIEWPORTS
============================================================ */

@media (max-width: 600px) {
  .sidebar-shell--open {
    width: min(
      var(--sidebar-total-width),
      100vw
    );
  }

  .sidebar-panel {
    width: min(
      var(--sidebar-panel-width),
      calc(100vw - 64px)
    );
    min-width: min(
      var(--sidebar-panel-width),
      calc(100vw - 64px)
    );
    max-width: calc(100vw - 64px);
  }

  .sidebar-resize-handle {
    display: none;
  }
}
</style>
